import { CefrLevelSchema, ChunkTypeSchema, type CefrLevel, type Chunk, type ChunkType } from "@engo/shared";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { Textarea } from "../ui/textarea.js";

export interface ChunkDraft {
  text: string;
  chunk_type: ChunkType;
  cefr_level: CefrLevel | "";
  example: string;
}

export const EMPTY_CHUNK_DRAFT: ChunkDraft = {
  text: "",
  chunk_type: "collocation",
  cefr_level: "",
  example: "",
};

const CHUNK_TYPES = ChunkTypeSchema.options;
const CEFR_LEVELS = CefrLevelSchema.options;

export function chunksToDrafts(chunks: Chunk[]): ChunkDraft[] {
  return chunks.map((c) => ({
    text: c.text,
    chunk_type: c.chunk_type,
    cefr_level: c.cefr_level ?? "",
    example: c.example,
  }));
}

export function draftsToChunks(drafts: ChunkDraft[]): Chunk[] {
  return drafts
    .filter((d) => d.text.trim() && d.example.trim())
    .map((d) => ({
      text: d.text.trim(),
      chunk_type: d.chunk_type,
      example: d.example.trim(),
      ...(d.cefr_level ? { cefr_level: d.cefr_level } : {}),
    }));
}

interface ChunkEditorProps {
  value: ChunkDraft[];
  onChange: (next: ChunkDraft[]) => void;
}

export function ChunkEditor({ value, onChange }: ChunkEditorProps) {
  const { t } = useTranslation();

  function updateChunk(index: number, patch: Partial<ChunkDraft>) {
    onChange(value.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  function addChunk() {
    onChange([...value, { ...EMPTY_CHUNK_DRAFT }]);
  }

  function removeChunk(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-muted)]">{t("chunkEditor.hint")}</p>
      {value.map((chunk, index) => (
        <div key={index} className="space-y-2 rounded-lg border border-[var(--glass-border)] p-3">
          <div className="grid gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
            <Input
              placeholder={t("chunkEditor.textPlaceholder")}
              value={chunk.text}
              onChange={(e) => updateChunk(index, { text: e.target.value })}
            />
            <select
              value={chunk.chunk_type}
              onChange={(e) => updateChunk(index, { chunk_type: e.target.value as ChunkType })}
              className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
            >
              {CHUNK_TYPES.map((ct) => (
                <option key={ct} value={ct} className="bg-[#0f172a]">
                  {t(`chunkType.${ct}`)}
                </option>
              ))}
            </select>
            <select
              value={chunk.cefr_level}
              onChange={(e) => updateChunk(index, { cefr_level: e.target.value as CefrLevel | "" })}
              className="w-full rounded-lg border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/25"
            >
              <option value="" className="bg-[#0f172a]">
                {t("chunkEditor.noLevel")}
              </option>
              {CEFR_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl} className="bg-[#0f172a]">
                  {lvl}
                </option>
              ))}
            </select>
            <Button type="button" variant="secondary" onClick={() => removeChunk(index)}>
              {t("chunkEditor.remove")}
            </Button>
          </div>
          <Textarea
            rows={2}
            placeholder={t("chunkEditor.examplePlaceholder")}
            value={chunk.example}
            onChange={(e) => updateChunk(index, { example: e.target.value })}
          />
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addChunk}>
        {t("chunkEditor.add")}
      </Button>
    </div>
  );
}
